import { useState } from 'react';
import { Alert, Grid } from '@methanesat/ui-components';
import { PipelineFeatureProperties, PointOGIMGeoJSONProperties } from '../../../../../types';
import { OGIDownloadButton } from './OGIDownloadButton';

export const OGIDownload = ({
    disabled,
    coordinates,
    details,
    trackDownload
}: {
    disabled: boolean;
    coordinates: number[];
    details: PipelineFeatureProperties[] | PointOGIMGeoJSONProperties[] | undefined;
    trackDownload?: () => void;
}) => {
    // selected keys for download
    const expectedKeys = [
        'layerName',
        'state_prov',
        'country',
        'fac_id',
        'fac_name',
        'fac_status',
        'fac_type',
        'operator_name',
        'install_date',
        'gas_capacity_mmcfd',
        'gas_throughput_mmcfd',
        'liq_capacity_bpd',
        'liq_throughput_bpd',
        'pipe_diameter_mm',
        'pipe_length_km',
        'pipe_material',
        'drill_type',
        'flare_year',
        'gas_flared_mmcf',
        'num_compr_units',
        'num_storage_tanks',
        'segment_type',
        'spud_date',
        'src_name',
        'src_url'
    ];

    // Alert states
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [alertSeverity, setAlertSeverity] = useState<'error' | 'info' | 'success' | 'warning'>('info');

    // handles downloadable ogi data
    const handleDownload = async () => {
        // no details or details array is empty
        if (!details || details.length === 0) {
            setAlertMessage('No data available for download.');
            setAlertSeverity('error');
            return;
        }

        // map data values to specified keys
        const data = details
            ? details.map((detail) => {
                  const obj: Record<string, string | number> = {};
                  for (const key of expectedKeys) {
                      // Check if the key exists at the top level of the current 'detail' object
                      if (key in detail) {
                          const value = detail[key as keyof typeof detail];
                          obj[key] = typeof value === 'string' || typeof value === 'number' ? value : '';
                          // Check if the key exists in the nested 'details' object of the current 'detail'
                      } else if (detail.details && key in detail.details) {
                          const value = detail.details[key as keyof typeof detail.details];
                          obj[key] = typeof value === 'string' || typeof value === 'number' ? value : '';
                          // Check if the key exists in the first object of the 'operators' array within the current 'detail'
                      } else if (detail.operators && detail.operators[0] && key in detail.operators[0]) {
                          const value = detail.operators[0][key as keyof (typeof detail.operators)[0]];
                          obj[key] = typeof value === 'string' || typeof value === 'number' ? value : '';
                          // Check if the key exists in the first object of the 'sources' array within the current 'detail'
                      } else if (detail.sources && detail.sources[0] && key in detail.sources[0]) {
                          const value = detail.sources[0][key as keyof (typeof detail.sources)[0]];
                          obj[key] = typeof value === 'string' || typeof value === 'number' ? value : '';
                          // key does not exist in the current 'detail' object
                      } else {
                          obj[key] = '';
                      }
                  }
                  return obj;
              })
            : [];

        // error in data mapping (no valid data)
        if (data.length === 0) {
            setAlertMessage('No valid data to download.');
            setAlertSeverity('error');
            return;
        }

        // mapping to valid csv format (string)
        const headerRow = expectedKeys.join(',') + '\r\n';

        let csvData = headerRow;

        for (let i = 0; i < data.length; i++) {
            let row = '';
            for (const key of expectedKeys) {
                const cellData = data[i][key];
                if (row !== '') row += ',';
                if (typeof cellData === 'string' && cellData.includes(',')) {
                    // wrap in double quotes & escape any double quotes in the string itself
                    row += `"${cellData.replace(/"/g, '""')}"`;
                } else {
                    row += cellData;
                }
            }
            csvData += row + '\r\n';
        }

        const url = URL.createObjectURL(new Blob([csvData], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `ogi_near_${coordinates[0]}_${coordinates[1]}.csv`;
        document.body.append(link);
        link.click();
        document.body.removeChild(link);

        // analytics tracking for download event
        trackDownload?.();

        // File download initiated alert
        setAlertMessage('CSV downloaded initiated successfully.');
        setAlertSeverity('success');
    };

    return (
        <Grid container direction="column">
            {/* OGIDownloadButton triggers the download */}
            <Grid size={{ xs: 12 }} display="flex" justifyContent="center">
                <OGIDownloadButton
                    data-testid="ogi-download"
                    onClick={handleDownload}
                    disabled={disabled || alertSeverity === 'success'}
                />
            </Grid>

            {/* Alert visible after download or if there's an error */}
            {alertMessage && (
                <Grid size={{ xs: 12 }} display="flex" justifyContent="center">
                    <Alert onClose={() => setAlertMessage(null)} severity={alertSeverity}>
                        {alertMessage}
                    </Alert>
                </Grid>
            )}
        </Grid>
    );
};
